import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { EIconCategory, getAppIcon } from '@src/helper/iconRegistry';
import type Employee from '@src/Objects/Employee';
import React, { useMemo } from 'react';

interface EmployeeOverviewTilesProps {
    employee: Employee;
    layout?: 'desktop' | 'mobile' | 'auto';
    hideEmptyTiles?: boolean;
}

interface TileData {
    title: string;
    value: string;
    icon: React.ReactElement;
    color: string;
    bgColor: string;
    borderColor: string;
    isText?: boolean;
    isEmpty?: boolean;
}

function EmployeeOverviewTiles({ employee, layout = 'auto', hideEmptyTiles = false }: EmployeeOverviewTilesProps) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
    const actualLayout = layout === 'auto' ? (isMobile ? 'mobile' : 'desktop') : layout;

    const tiles: TileData[] = useMemo(() => {
        const skills = employee.skills || [];
        const certificates = employee.certificates || [];
        const competences = employee.competences || [];

        // // Calculate average skill level (1-4 scale)
        // const avgSkillLevel =
        //     skills.length > 0 ? skills.reduce((sum, skill) => sum + skill.level, 0) / skills.length : 0;

        // Find superpowers (skills marked as SuperPowers)
        const superPowers = skills.filter((skill) => skill.isSuperPower === true);

        const allTiles = [
            {
                title: 'Skills',
                value: skills.length.toString(),
                icon: React.cloneElement(getAppIcon(EIconCategory.SKILLS), { size: 32, strokeWidth: 1.5 }),
                color: theme.palette.primary.main,
                bgColor: theme.palette.tileBackground?.primary || `${theme.palette.primary.main}15`,
                borderColor: theme.palette.tileBorder?.primary || `${theme.palette.primary.main}90`,
                isEmpty: skills.length === 0,
            },
            {
                title: 'Super Powers',
                value: superPowers.length.toString(),
                icon: React.cloneElement(getAppIcon(EIconCategory.SUPERPOWERS), { size: 32, strokeWidth: 1.5 }),
                color: theme.palette.accent?.main || '#ffb74d',
                bgColor: theme.palette.tileBackground?.accent || `${theme.palette.accent?.main || '#ffb74d'}15`,
                borderColor: theme.palette.tileBorder?.accent || `${theme.palette.accent?.main || '#ffb74d'}90`,
                isText: false,
                isEmpty: superPowers.length === 0,
            },
            {
                title: 'Zertifikate',
                value: certificates.length.toString(),
                icon: React.cloneElement(getAppIcon(EIconCategory.CERTIFICATES), { size: 32, strokeWidth: 1.5 }),
                color: theme.palette.secondary.main,
                bgColor: theme.palette.tileBackground?.secondary || `${theme.palette.secondary.main}15`,
                borderColor: theme.palette.tileBorder?.secondary || `${theme.palette.secondary.main}90`,
                isEmpty: certificates.length === 0,
            },
            {
                title: 'Kompetenzen',
                value: competences.length.toString(),
                icon: React.cloneElement(getAppIcon(EIconCategory.COMPETENCES), { size: 32, strokeWidth: 1.5 }),
                color: theme.palette.primary.main,
                bgColor: theme.palette.tileBackground?.primary || `${theme.palette.primary.main}15`,
                borderColor: theme.palette.tileBorder?.primary || `${theme.palette.primary.main}90`,
                isEmpty: competences.length === 0,
            },
            {
                title: 'Missionen',
                value: '0', // MockUp value from Missions Tab //TODO: IMPLEMENT
                icon: React.cloneElement(getAppIcon(EIconCategory.MISSIONS), { size: 32, strokeWidth: 1.5 }),
                color: theme.palette.secondary.main,
                bgColor: theme.palette.tileBackground?.secondary || `${theme.palette.secondary.main}15`,
                borderColor: theme.palette.tileBorder?.secondary || `${theme.palette.secondary.main}90`,
                isEmpty: false,
            },
        ];

        return hideEmptyTiles ? allTiles.filter((tile) => !tile.isEmpty) : allTiles;
    }, [employee, theme, hideEmptyTiles]);

    if (actualLayout === 'mobile') {
        return (
            <Box
                sx={{
                    display: 'flex',
                    overflowX: 'auto',
                    gap: 2,
                    mb: 3,
                    pb: 1,
                    '&::-webkit-scrollbar': {
                        display: 'none',
                    },
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                }}
            >
                {tiles.map((tile) => (
                    <Box
                        key={tile.title}
                        sx={{
                            p: 2,
                            borderRadius: '32px 4px',
                            backgroundColor: tile.bgColor,
                            border: `2px solid ${tile.borderColor}`,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            textAlign: 'center',
                            minHeight: 100,
                            minWidth: 120,
                            flexShrink: 0,
                        }}
                    >
                        <Box
                            sx={{
                                color: tile.color,
                                mb: 1,
                            }}
                        >
                            {tile.icon}
                        </Box>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 600,
                                color: theme.palette.text.primary,
                                mb: 0.5,
                                fontSize: tile.isText ? '0.875rem' : '1.5rem',
                                lineHeight: tile.isText ? 1.2 : 1,
                            }}
                        >
                            {tile.value}
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{
                                color: theme.palette.text.secondary,
                                fontWeight: 500,
                            }}
                        >
                            {tile.title}
                        </Typography>
                    </Box>
                ))}
            </Box>
        );
    }

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: {
                    xs: 'repeat(2, 1fr)',
                    sm: 'repeat(3, 1fr)',
                    md: 'repeat(4, 1fr)',
                    lg: 'repeat(5, 1fr)',
                },
                gap: { xs: 0.5, md: 1, lg: 1.5 },
                mb: 3,
            }}
        >
            {tiles.map((tile) => (
                <Box
                    key={tile.title}
                    sx={{
                        p: { xs: 1, md: 2 },
                        borderRadius: '32px 4px',
                        backgroundColor: tile.bgColor,
                        border: `2px solid ${tile.borderColor}`,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center',
                        minHeight: 100,
                    }}
                >
                    <Box
                        sx={{
                            color: tile.color,
                            mb: 1,
                        }}
                    >
                        {tile.icon}
                    </Box>
                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: 600,
                            color: theme.palette.text.primary,
                            mb: 0.5,
                            fontSize: tile.isText ? '0.975rem' : '1.5rem',
                            lineHeight: tile.isText ? 1.2 : 1,
                        }}
                    >
                        {tile.value}
                    </Typography>
                    <Typography
                        variant="caption"
                        sx={{
                            color: theme.palette.text.secondary,
                            fontWeight: 500,
                        }}
                    >
                        {tile.title}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
}

export default EmployeeOverviewTiles;
